import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  message,
  Popconfirm,
  InputNumber,
  App,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  UploadOutlined,
  BulbOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Inspiration {
  id: string;
  type: 'VIDEO' | 'IMAGE';
  title: string;
  prompt: string;
  description?: string;
  thumbnail?: string;
  category: string;
  tags: string[];
  isPublic: boolean;
  sortOrder: number;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

// 优先级预设
const PRIORITY_LEVELS = {
  HIGHEST: { value: 100, label: '最高', color: 'red' },
  HIGH: { value: 80, label: '高', color: 'orange' },
  MEDIUM: { value: 50, label: '中', color: 'blue' },
  LOW: { value: 20, label: '低', color: 'default' },
  LOWEST: { value: 0, label: '最低', color: 'default' }
};

const getPriorityLevel = (sortOrder: number) => {
  if (sortOrder >= 100) return PRIORITY_LEVELS.HIGHEST;
  if (sortOrder >= 80) return PRIORITY_LEVELS.HIGH;
  if (sortOrder >= 50) return PRIORITY_LEVELS.MEDIUM;
  if (sortOrder >= 20) return PRIORITY_LEVELS.LOW;
  return PRIORITY_LEVELS.LOWEST;
};

const CATEGORIES = [
  { value: 'GENERAL', label: '通用' },
  { value: 'NATURE', label: '自然风景' },
  { value: 'PORTRAIT', label: '人物肖像' },
  { value: 'ABSTRACT', label: '抽象艺术' },
  { value: 'CINEMATIC', label: '电影风格' },
  { value: 'ANIME', label: '动漫风格' },
  { value: 'ARCHITECTURE', label: '建筑' },
  { value: 'FOOD', label: '美食' },
  { value: 'ANIMAL', label: '动物' },
  { value: 'TECHNOLOGY', label: '科技' }
];

const InspirationManagement: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Inspiration[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [stats, setStats] = useState({
    totalVideo: 0,
    totalImage: 0,
    totalPublic: 0,
    totalUsage: 0
  });

  // 加载数据
  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = { page, pageSize };
      if (filterType) params.type = filterType;
      if (filterCategory) params.category = filterCategory;

      console.log('📥 [灵感管理] 开始加载数据...', params);

      const response = await apiClient.get('/admin/inspiration/list', { 
        params,
        timeout: 60000  // 增加超时时间到60秒
      });
      
      console.log('✅ [灵感管理] 加载成功，响应:', response);
      
      // apiClient已经返回了response.data，所以response就是{success, data}
      if (response.success) {
        const items = (response.data?.list || []).map((item: any) => ({
          ...item,
          tags: typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags
        }));
        setData(items);
        setTotal(response.data?.total || 0);
        console.log(`✅ [灵感管理] 数据加载完成: ${items.length} 条记录`);
      }
    } catch (error: any) {
      console.error('❌ [灵感管理] 加载失败:', error);
      
      // 更友好的错误处理
      let errorMessage = '加载失败';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = '请求超时，数据量较大或网络较慢，请稍后重试';
      } else if (error.response?.status === 404) {
        errorMessage = 'API接口不存在，请检查后端服务';
      } else if (error.response?.status === 500) {
        errorMessage = '服务器错误，请联系管理员';
      } else {
        errorMessage = error.response?.data?.message || error.message || '未知错误';
      }
      
      messageApi.error(errorMessage);
      // 设置空数据避免界面报错
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 加载统计
  const loadStats = async () => {
    try {
      console.log('📊 [灵感管理] 开始加载统计...');
      const response = await apiClient.get('/admin/inspiration/stats', {
        timeout: 60000  // 增加超时时间到60秒
      });
      console.log('✅ [灵感管理] 统计加载成功:', response);
      
      // apiClient已经返回了response.data
      if (response.success) {
        setStats(response.data || { totalVideo: 0, totalImage: 0, totalPublic: 0, totalUsage: 0 });
      }
    } catch (error: any) {
      console.error('⚠️ [灵感管理] 加载统计失败:', error);
      // 统计失败不影响主要功能，静默处理
      setStats({ totalVideo: 0, totalImage: 0, totalPublic: 0, totalUsage: 0 });
      
      // 只在是严重错误时才提示
      if (error.response?.status === 500) {
        messageApi.warning('统计数据加载失败，但不影响灵感管理功能');
      }
    }
  };

  useEffect(() => {
    loadData();
    loadStats();
  }, [page, pageSize, filterType, filterCategory]);

  // 打开创建/编辑对话框
  const handleOpenModal = (record?: Inspiration) => {
    if (record) {
      setEditingId(record.id);
      form.setFieldsValue({
        ...record,
        tags: record.tags
      });
    } else {
      setEditingId(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingId) {
        // 更新
        await apiClient.put(`/admin/inspiration/${editingId}`, values);
        messageApi.success('更新成功');
      } else {
        // 创建
        await apiClient.post('/admin/inspiration', values);
        messageApi.success('创建成功');
      }

      setModalVisible(false);
      loadData();
      loadStats();
    } catch (error: any) {
      messageApi.error('保存失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 删除
  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await apiClient.delete(`/admin/inspiration/${id}`);
      messageApi.success('删除成功');
      loadData();
      loadStats();
    } catch (error: any) {
      messageApi.error('删除失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 批量删除
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      messageApi.warning('请选择要删除的项');
      return;
    }

    try {
      setLoading(true);
      await apiClient.post('/admin/inspiration/batch-delete', {
        ids: selectedRowKeys
      });
      messageApi.success(`成功删除${selectedRowKeys.length}条记录`);
      setSelectedRowKeys([]);
      loadData();
      loadStats();
    } catch (error: any) {
      messageApi.error('批量删除失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 切换公开状态
  const handleTogglePublic = async (id: string) => {
    try {
      await apiClient.put(`/admin/inspiration/${id}/toggle-public`);
      messageApi.success('状态已更新');
      loadData();
      loadStats();
    } catch (error: any) {
      messageApi.error('操作失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 调整排序
  const handleUpdateSort = async (id: string, sortOrder: number) => {
    try {
      await apiClient.put(`/admin/inspiration/${id}/sort`, { sortOrder });
      messageApi.success('排序已更新');
      loadData();
    } catch (error: any) {
      messageApi.error('操作失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 创建示例数据
  const handleCreateSamples = async (force: boolean = false) => {
    try {
      setLoading(true);
      const response = await apiClient.post('/admin/inspiration/create-samples', { force });
      
      console.log('创建示例数据响应:', response);
      
      // apiClient已经返回了response.data
      if (response.success) {
        messageApi.success(response.message || '示例数据创建成功');
        loadData();
        loadStats();
      } else if (response.success === false && response.message) {
        // 询问是否强制创建
        Modal.confirm({
          title: '已存在灵感数据',
          content: response.message + '\n\n是否要清空现有数据并重新创建示例？',
          okText: '是，重新创建',
          cancelText: '取消',
          onOk: () => handleCreateSamples(true)
        });
      } else {
        messageApi.error('创建失败: 未知错误');
      }
    } catch (error: any) {
      console.error('创建示例数据失败:', error);
      messageApi.error('创建失败: ' + (error.response?.data?.message || error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Inspiration> = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'VIDEO' ? 'blue' : 'green'} icon={type === 'VIDEO' ? <VideoCameraOutlined /> : <PictureOutlined />}>
          {type === 'VIDEO' ? '视频' : '图片'}
        </Tag>
      )
    },
    {
      title: '缩略图',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      width: 80,
      render: (thumbnail: string) => thumbnail ? (
        <img src={thumbnail} alt="缩略图" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />
      ) : (
        <div style={{ width: 50, height: 50, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PictureOutlined style={{ color: '#999' }} />
        </div>
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true
    },
    {
      title: '提示词',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 300 }}>
          {text}
        </Text>
      )
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => {
        const cat = CATEGORIES.find(c => c.value === category);
        return <Tag>{cat?.label || category}</Tag>;
      }
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => (
        <>
          {tags.slice(0, 3).map(tag => (
            <Tag key={tag} style={{ marginBottom: 4 }}>{tag}</Tag>
          ))}
          {tags.length > 3 && <Tag>+{tags.length - 3}</Tag>}
        </>
      )
    },
    {
      title: '状态',
      dataIndex: 'isPublic',
      key: 'isPublic',
      width: 80,
      render: (isPublic: boolean, record) => (
        <Switch
          checked={isPublic}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
          onChange={() => handleTogglePublic(record.id)}
        />
      )
    },
    {
      title: '优先级',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 180,
      sorter: (a, b) => a.sortOrder - b.sortOrder,
      render: (sortOrder: number, record) => {
        const priority = getPriorityLevel(sortOrder);
        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div className="flex items-center gap-2">
              <Tag color={priority.color} style={{ minWidth: 60, textAlign: 'center' }}>
                {priority.label}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({sortOrder})
              </Text>
            </div>
            <Space size="small">
              <Select
                size="small"
                value={sortOrder}
                style={{ width: 90 }}
                onChange={(value) => handleUpdateSort(record.id, value)}
              >
                <Select.Option value={100}>
                  <Tag color="red">最高</Tag>
                </Select.Option>
                <Select.Option value={80}>
                  <Tag color="orange">高</Tag>
                </Select.Option>
                <Select.Option value={50}>
                  <Tag color="blue">中</Tag>
                </Select.Option>
                <Select.Option value={20}>
                  <Tag color="default">低</Tag>
                </Select.Option>
                <Select.Option value={0}>
                  <Tag color="default">最低</Tag>
                </Select.Option>
              </Select>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                onClick={() => handleUpdateSort(record.id, Math.min(999, sortOrder + 10))}
                title="提升10"
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                onClick={() => handleUpdateSort(record.id, Math.max(0, sortOrder - 10))}
                title="降低10"
              />
            </Space>
          </Space>
        );
      }
    },
    {
      title: '使用次数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      sorter: (a, b) => a.usageCount - b.usageCount
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这条灵感吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* 顶部提示卡片 - 当没有数据时显示 */}
      {total === 0 && (
        <Card
          style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          bodyStyle={{ padding: '24px' }}
        >
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div className="mb-4">
              <span className="text-6xl">💡</span>
            </div>
            <Title level={3} className="bg-gradient-to-r from-yellow-200 via-orange-200 to-pink-200 bg-clip-text text-transparent mb-3">
              欢迎使用热门创作灵感管理
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.95)', display: 'block', marginBottom: 24, fontSize: '15px' }}>
              当前还没有灵感数据，快速开始：一键创建16个精选示例（8个视频 + 8个图片）
            </Text>
            <Button
              size="large"
              icon={<PlusOutlined />}
              onClick={() => handleCreateSamples(false)}
              loading={loading}
              style={{
                background: 'white',
                color: '#667eea',
                fontWeight: 'bold',
                height: 48,
                fontSize: 16,
                borderRadius: 24,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              🎨 创建示例灵感数据
            </Button>
          </div>
        </Card>
      )}

      {/* 页面标题 - 美化版 */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-2">
          <span className="bg-gradient-to-r from-yellow-600 via-orange-600 to-pink-600 bg-clip-text text-transparent">
            💡 热门创作灵感管理
          </span>
        </h2>
        <p className="text-gray-600">管理视频和图片生成的热门提示词灵感，引导用户快速创作</p>
      </div>

      {/* 统计卡片 - 渐变美化版 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} lg={6}>
          <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-100 hover:shadow-orange-200 transition-all duration-300">
            <Statistic
              title={<span className="text-gray-700 font-bold text-sm">💡 总灵感数</span>}
              value={stats.totalVideo + stats.totalImage}
              valueStyle={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                background: 'linear-gradient(to right, #f59e0b, #f97316)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 hover:shadow-blue-200 transition-all duration-300">
            <Statistic
              title={<span className="text-gray-700 font-bold text-sm">🎬 视频灵感</span>}
              value={stats.totalVideo}
              valueStyle={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                background: 'linear-gradient(to right, #3b82f6, #6366f1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100 hover:shadow-pink-200 transition-all duration-300">
            <Statistic
              title={<span className="text-gray-700 font-bold text-sm">🎨 图片灵感</span>}
              value={stats.totalImage}
              valueStyle={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                background: 'linear-gradient(to right, #ec4899, #f43f5e)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 hover:shadow-purple-200 transition-all duration-300">
            <Statistic
              title={<span className="text-gray-700 font-bold text-sm">📢 公开数量</span>}
              value={stats.totalPublic}
              suffix={<span style={{ fontSize: '1rem', color: '#9333ea' }}>/ {stats.totalVideo + stats.totalImage}</span>}
              valueStyle={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                background: 'linear-gradient(to right, #9333ea, #a855f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        className="shadow-xl rounded-2xl border-0"
        extra={
          <Space>
            <Select
              placeholder="类型筛选"
              style={{ width: 120 }}
              allowClear
              value={filterType}
              onChange={setFilterType}
            >
              <Select.Option value="VIDEO">📹 视频</Select.Option>
              <Select.Option value="IMAGE">🖼️ 图片</Select.Option>
            </Select>
            <Select
              placeholder="分类筛选"
              style={{ width: 140 }}
              allowClear
              value={filterCategory}
              onChange={setFilterCategory}
            >
              {CATEGORIES.map(cat => (
                <Select.Option key={cat.value} value={cat.value}>
                  {cat.label}
                </Select.Option>
              ))}
            </Select>
            <Tooltip title="刷新列表">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadData();
                  loadStats();
                }}
                loading={loading}
              >
                刷新
              </Button>
            </Tooltip>
            <Button
              icon={<BulbOutlined />}
              onClick={() => handleCreateSamples(false)}
              disabled={loading}
              style={{ marginRight: 8 }}
            >
              创建示例数据
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              创建灵感
            </Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除 ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1500 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            }
          }}
        />
      </Card>

      {/* 创建/编辑对话框 */}
      <Modal
        title={editingId ? '编辑灵感' : '创建灵感'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        width={800}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: 'VIDEO',
            category: 'GENERAL',
            isPublic: true,
            sortOrder: 50,  // 默认中优先级
            tags: []
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="类型"
                name="type"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select>
                  <Select.Option value="VIDEO">
                    <VideoCameraOutlined /> 视频
                  </Select.Option>
                  <Select.Option value="IMAGE">
                    <PictureOutlined /> 图片
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="分类"
                name="category"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select>
                  {CATEGORIES.map(cat => (
                    <Select.Option key={cat.value} value={cat.value}>
                      {cat.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="标题"
            name="title"
            rules={[
              { required: true, message: '请输入标题' },
              { max: 100, message: '标题最多100字符' }
            ]}
          >
            <Input placeholder="例如：美丽的日落风景" />
          </Form.Item>

          <Form.Item
            label="提示词"
            name="prompt"
            rules={[
              { required: true, message: '请输入提示词' },
              { max: 2000, message: '提示词最多2000字符' }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="输入完整的提示词内容"
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[{ max: 500, message: '描述最多500字符' }]}
          >
            <TextArea
              rows={2}
              placeholder="简短描述这个灵感（可选）"
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            label="缩略图URL"
            name="thumbnail"
            rules={[{ type: 'url', message: '请输入有效的URL' }]}
          >
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Form.Item
            label="标签"
            name="tags"
          >
            <Select
              mode="tags"
              placeholder="输入标签后按回车添加"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="优先级"
                name="sortOrder"
                tooltip="优先级越高，在生成页面越靠前显示"
              >
                <Select style={{ width: '100%' }}>
                  <Select.Option value={100}>
                    <Tag color="red">最高优先级</Tag> (100)
                  </Select.Option>
                  <Select.Option value={80}>
                    <Tag color="orange">高优先级</Tag> (80)
                  </Select.Option>
                  <Select.Option value={50}>
                    <Tag color="blue">中优先级</Tag> (50)
                  </Select.Option>
                  <Select.Option value={20}>
                    <Tag color="default">低优先级</Tag> (20)
                  </Select.Option>
                  <Select.Option value={0}>
                    <Tag color="default">最低优先级</Tag> (0)
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="公开显示"
                name="isPublic"
                valuePropName="checked"
              >
                <Switch checkedChildren="公开" unCheckedChildren="隐藏" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default InspirationManagement;

